import {
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
  type ReactNode,
} from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InvoicePreviewLabels {
  invoice: string;
  balanceDue: string;
  billTo: string;
  invoiceDate: string;
  terms: string;
  dueDate: string;
  description: string;
  quantity: string;
  rate: string;
  amount: string;
  noItems: string;
  subtotal: string;
  shippingCosts: string;
  discount: string;
  vat: string;
  /** Links a VAT rate to its base: "TVA 8.1 % sur CHF 800.00". */
  vatOn: string;
  total: string;
  bankDetails: string;
  yourLogo: string;
  qrBill: string;
}

const DEFAULT_LABELS: InvoicePreviewLabels = {
  invoice: 'Facture',
  balanceDue: 'Solde dû',
  billTo: 'Facturer à',
  invoiceDate: 'Date de facture',
  terms: 'Conditions',
  dueDate: "Date d'échéance",
  description: 'Description',
  quantity: 'Quantité',
  rate: 'Taux',
  amount: 'Montant',
  noItems: 'Aucun article',
  subtotal: 'Sous-total',
  shippingCosts: 'Frais de livraison',
  discount: 'Remise',
  vat: 'TVA',
  vatOn: 'sur',
  total: 'Total',
  bankDetails: 'Coordonnées bancaires',
  yourLogo: 'VOTRE LOGO',
  qrBill: 'Swiss QR-Bill',
};

export interface InvoicePreviewProps {
  logoUrl: string | null;
  accentColor: string;
  imageScale?: number;
  companyName?: string;
  companyAddress?: string;
  companyPostalCode?: string;
  companyCity?: string;
  companyPhone?: string;
  companyIdeNumber?: string;
  companyVatNumber?: string;
  paymentTerms?: string;
  thankYouNote?: string;
  footerNote?: string;
  bankIban?: string;
  bankBic?: string;
  /** When true, show a compact footer and a dashed QR-Bill placeholder zone at the bottom. */
  showQrBillZone?: boolean;
  /** Translated labels for the preview. French defaults are used when omitted. */
  labels?: Partial<InvoicePreviewLabels>;
  /** Optional acceptance block rendered after the totals (quotes only). */
  signatureBlock?: {
    title: string;
    dateLabel: string;
    signatureLabel: string;
  };
  // Real bill data (optional — when omitted, sample data is used)
  invoiceNumber?: string;
  clientName?: string;
  clientAddress?: string;
  clientPostalCode?: string;
  clientCity?: string;
  clientCountry?: string;
  clientIdeNumber?: string;
  clientVatNumber?: string;
  issueDate?: string;
  dueDate?: string;
  currency?: string;
  lineItems?: {
    num: number;
    description: string;
    descriptionSegments?: InvoicePreviewTextSegment[];
    qty: number;
    rate: number;
    /** Shown when `vatBreakdown` has several rates. */
    vatRate?: number;
  }[];
  subtotal?: number;
  vatAmount?: number;
  /**
   * One totals row per rate, plus a VAT column on the lines when there are
   * several. Without it, `vatAmount` shows alone.
   */
  vatBreakdown?: { rate: number; base: number; vat: number }[];
  /** Leaves VAT out of the document altogether. */
  hideVat?: boolean;
  discount?: number;
  shippingCosts?: number;
  total?: number;
}

export interface InvoicePreviewTextSegment {
  text: string;
  bold?: true;
  italic?: true;
  underline?: true;
}

function renderTextSegment(
  segment: InvoicePreviewTextSegment,
  key: number,
): ReactNode {
  let content: ReactNode = segment.text;
  if (segment.underline) content = <u>{content}</u>;
  if (segment.italic) content = <em>{content}</em>;
  if (segment.bold) content = <strong>{content}</strong>;
  return <span key={key}>{content}</span>;
}

// ---------------------------------------------------------------------------
// Sample data
// ---------------------------------------------------------------------------

const LINE_ITEMS = [
  {
    num: 1,
    description: 'Consultation stratégie digitale',
    qty: 4,
    rate: 150,
  },
  {
    num: 2,
    description: 'Développement site web — forfait',
    qty: 1,
    rate: 3200,
  },
  {
    num: 3,
    description: 'Maintenance & hébergement (mensuel)',
    qty: 3,
    rate: 85,
  },
];

function formatAmount(amount: number, currency = 'CHF') {
  return new Intl.NumberFormat('de-CH', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

// ---------------------------------------------------------------------------
// Small helper components
// ---------------------------------------------------------------------------

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span style={{ color: '#999', marginRight: 8 }}>{label}:</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function TotalRow({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 9,
        padding: '2px 0',
        fontWeight: bold ? 600 : 400,
        color: bold ? '#222' : '#555',
      }}
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const thStyle: React.CSSProperties = {
  padding: '6px 8px',
  fontWeight: 600,
  fontSize: 8,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

const tdStyle: React.CSSProperties = {
  padding: '7px 8px',
};

const A4_WIDTH = 595;
const A4_MIN_HEIGHT = 842;
const A4_QR_BILL_ESTIMATED_HEIGHT = 949;

const useBrowserLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

export const invoicePreviewStyles: Record<string, React.CSSProperties> = {
  previewContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: 0,
    overflow: 'visible',
    width: '100%',
    minWidth: 0,
  },
  a4: {
    width: A4_WIDTH,
    minHeight: A4_MIN_HEIGHT,
    background: '#fff',
    padding: '40px 45px',
    boxShadow: '0 2px 20px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  },
};

// ---------------------------------------------------------------------------
// InvoicePreview
// ---------------------------------------------------------------------------

export function InvoicePreview({
  logoUrl,
  accentColor,
  imageScale = 1,
  companyName = 'WAP Sàrl',
  companyAddress = 'Pré-Puceret 14',
  companyPostalCode = '1564',
  companyCity = 'Domdidier',
  companyPhone = '+41 79 123 45 67',
  companyIdeNumber,
  companyVatNumber = 'CHE-123.456.789 TVA',
  paymentTerms = '30 jours',
  thankYouNote = 'Merci de votre confiance.',
  footerNote,
  bankIban = 'CH93 0076 2011 6238 5295 7',
  bankBic = 'BCVLCH2L',
  showQrBillZone = false,
  labels: labelsProp,
  signatureBlock,
  invoiceNumber,
  clientName,
  clientAddress,
  clientPostalCode,
  clientCity,
  clientCountry,
  clientIdeNumber,
  clientVatNumber,
  issueDate,
  dueDate,
  currency = 'CHF',
  lineItems,
  subtotal: subtotalProp,
  vatAmount: vatAmountProp,
  vatBreakdown = [],
  hideVat = false,
  discount,
  shippingCosts,
  total: totalProp,
}: InvoicePreviewProps) {
  const l = { ...DEFAULT_LABELS, ...labelsProp };
  const containerRef = useRef<HTMLDivElement>(null);
  const a4Ref = useRef<HTMLDivElement>(null);
  const [metrics, setMetrics] = useState<{
    scale: number;
    a4Height: number;
  } | null>(null);

  useBrowserLayoutEffect(() => {
    const container = containerRef.current;
    const a4 = a4Ref.current;
    if (!container || !a4) return;

    const measure = () => {
      const width = container.getBoundingClientRect().width;
      const a4Height = a4.offsetHeight;
      if (width <= 0 || a4Height <= 0) return;

      const scale = Math.min(width / A4_WIDTH, 1.3);
      setMetrics((current) => {
        if (
          current &&
          Math.abs(current.scale - scale) < 0.001 &&
          current.a4Height === a4Height
        ) {
          return current;
        }
        return { scale, a4Height };
      });
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(container);
    ro.observe(a4);
    return () => ro.disconnect();
  }, []);

  const items: NonNullable<InvoicePreviewProps['lineItems']> =
    lineItems ?? LINE_ITEMS;
  const hasNoItems = lineItems !== undefined && lineItems.length === 0;
  const computedSubtotal = items.reduce((s, i) => s + i.qty * i.rate, 0);
  const subtotal = subtotalProp ?? computedSubtotal;
  const tva = vatAmountProp ?? 0;
  const total = totalProp ?? subtotal + tva;
  const vatRows = hideVat ? [] : vatBreakdown;
  const showVatColumn = vatRows.length > 1;
  const fmt = (amount: number) => formatAmount(amount, currency);
  const logoWidth = 120 * imageScale;
  const isMeasured = metrics !== null;
  const scale = metrics?.scale ?? 1;
  const a4Height =
    metrics?.a4Height ??
    (showQrBillZone ? A4_QR_BILL_ESTIMATED_HEIGHT : A4_MIN_HEIGHT);
  const scaledA4Width = A4_WIDTH * scale;
  const scaledA4Height = a4Height * scale;

  return (
    <div
      ref={containerRef}
      style={invoicePreviewStyles.previewContainer}
      data-invoice-preview-state={isMeasured ? 'ready' : 'measuring'}
    >
      <div
        data-invoice-preview-sizer
        style={{
          width: isMeasured ? scaledA4Width : '100%',
          height: isMeasured ? scaledA4Height : undefined,
          aspectRatio: isMeasured ? undefined : `${A4_WIDTH} / ${a4Height}`,
          flexShrink: 0,
          maxWidth: '100%',
          position: 'relative',
          overflow: isMeasured ? 'visible' : 'hidden',
          background: '#fff',
          boxShadow: isMeasured ? undefined : invoicePreviewStyles.a4.boxShadow,
        }}
      >
        <div
          data-invoice-preview-canvas
          aria-hidden={!isMeasured}
          style={{
            position: 'absolute',
            inset: '0 auto auto 0',
            width: A4_WIDTH,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            visibility: isMeasured ? 'visible' : 'hidden',
          }}
        >
          <div ref={a4Ref} style={invoicePreviewStyles.a4}>
            {/* Header row */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 24,
              }}
            >
              {/* Left: logo */}
              <div style={{ minWidth: logoWidth, minHeight: 40 }}>
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Logo"
                    style={{
                      width: logoWidth,
                      height: 'auto',
                      objectFit: 'contain',
                    }}
                  />
                ) : companyName ? (
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: accentColor,
                      lineHeight: 1.2,
                    }}
                  >
                    {companyName}
                  </div>
                ) : (
                  <div
                    style={{
                      width: logoWidth,
                      height: 48,
                      border: '1.5px dashed #ccc',
                      borderRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#bbb',
                      fontSize: 9,
                    }}
                  >
                    {l.yourLogo}
                  </div>
                )}
              </div>

              {/* Right: Facture heading */}
              <div style={{ textAlign: 'right' }}>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: accentColor,
                    lineHeight: 1,
                  }}
                >
                  {l.invoice}
                </div>
                <div style={{ fontSize: 10, color: '#666', marginTop: 3 }}>
                  {invoiceNumber ?? 'INV-000071'}
                </div>
                <div
                  style={{
                    marginTop: 10,
                    fontSize: 9,
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  {l.balanceDue}
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: accentColor,
                    marginTop: 1,
                  }}
                >
                  {fmt(total)}
                </div>
              </div>
            </div>

            {/* Company info */}
            <div style={{ fontSize: 9, color: '#444', lineHeight: 1.6 }}>
              <strong style={{ fontSize: 10 }}>{companyName}</strong>
              <br />
              {companyAddress}
              <br />
              {companyPostalCode} {companyCity}
              <br />
              {companyPhone}
              {companyIdeNumber && (
                <>
                  <br />
                  {companyIdeNumber}
                </>
              )}
              {companyVatNumber && (
                <>
                  <br />
                  {`${l.vat}: ${companyVatNumber}`}
                </>
              )}
            </div>

            {/* Dates + client row */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 20,
                marginTop: 22,
                marginBottom: 22,
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 9,
                  color: '#444',
                  lineHeight: 1.8,
                }}
              >
                <Row label={l.invoiceDate} value={issueDate ?? '01/01/2026'} />
                {paymentTerms && <Row label={l.terms} value={paymentTerms} />}
                <Row label={l.dueDate} value={dueDate ?? '31/01/2026'} />
              </div>
              <div
                style={{
                  width: 220,
                  flexShrink: 0,
                  fontSize: 9,
                  color: '#444',
                  lineHeight: 1.6,
                  textAlign: 'right',
                }}
              >
                <div
                  style={{
                    fontSize: 8,
                    textTransform: 'uppercase',
                    color: '#999',
                    letterSpacing: 0.5,
                    marginBottom: 2,
                  }}
                >
                  {l.billTo}
                </div>
                <strong>{clientName ?? 'Dupont SA'}</strong>
                <br />
                {clientAddress ?? 'Route de Berne 42'}
                <br />
                {clientPostalCode ?? '1700'} {clientCity ?? 'Fribourg'} (
                {clientCountry ?? 'Suisse'})
                {clientIdeNumber && (
                  <>
                    <br />
                    {clientIdeNumber}
                  </>
                )}
                {clientVatNumber && (
                  <>
                    <br />
                    {`${l.vat}: ${clientVatNumber}`}
                  </>
                )}
              </div>
            </div>

            {/* Line items table */}
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 9,
                marginBottom: 18,
              }}
            >
              <thead>
                <tr
                  style={{
                    background: accentColor,
                    color: '#fff',
                    textAlign: 'left',
                  }}
                >
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>{l.description}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>
                    {l.quantity}
                  </th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{l.rate}</th>
                  {showVatColumn && (
                    <th style={{ ...thStyle, textAlign: 'right' }}>{l.vat}</th>
                  )}
                  <th style={{ ...thStyle, textAlign: 'right' }}>{l.amount}</th>
                </tr>
              </thead>
              <tbody>
                {hasNoItems ? (
                  <tr>
                    <td
                      colSpan={showVatColumn ? 6 : 5}
                      style={{
                        padding: '20px 8px',
                        textAlign: 'center',
                        color: '#999',
                        fontStyle: 'italic',
                      }}
                    >
                      {l.noItems}
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr
                      key={item.num}
                      style={{
                        borderBottom: '1px solid #eee',
                      }}
                    >
                      <td style={tdStyle}>{item.num}</td>
                      <td style={{ ...tdStyle, whiteSpace: 'pre-line' }}>
                        {item.descriptionSegments?.length
                          ? item.descriptionSegments.map(renderTextSegment)
                          : item.description}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {item.qty}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {fmt(item.rate)}
                      </td>
                      {showVatColumn && (
                        <td
                          style={{
                            ...tdStyle,
                            textAlign: 'right',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.vatRate !== undefined && `${item.vatRate} %`}
                        </td>
                      )}
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {fmt(item.qty * item.rate)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Totals */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginBottom: 28,
              }}
            >
              <div style={{ width: 220 }}>
                <TotalRow label={l.subtotal} value={fmt(subtotal)} />
                {(shippingCosts ?? 0) > 0 && (
                  <TotalRow
                    label={l.shippingCosts}
                    value={fmt(shippingCosts!)}
                  />
                )}
                {(discount ?? 0) > 0 && (
                  <TotalRow label={l.discount} value={`− ${fmt(discount!)}`} />
                )}
                {!hideVat && vatRows.length === 0 && (
                  <TotalRow label={l.vat} value={fmt(tva)} />
                )}
                {vatRows.map((row) => {
                  const rate = `${l.vat} ${row.rate} %`;
                  return (
                    <TotalRow
                      key={row.rate}
                      label={
                        showVatColumn
                          ? `${rate} ${l.vatOn} ${fmt(row.base)}`
                          : rate
                      }
                      value={fmt(row.vat)}
                    />
                  );
                })}
                <div
                  style={{
                    borderTop: `2px solid ${accentColor}`,
                    marginTop: 6,
                    paddingTop: 6,
                  }}
                >
                  <TotalRow label={l.total} value={fmt(total)} bold />
                </div>
                <div
                  style={{
                    marginTop: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 11,
                    fontWeight: 700,
                    color: accentColor,
                  }}
                >
                  <span>{l.balanceDue}</span>
                  <span>{fmt(total)}</span>
                </div>
              </div>
            </div>

            {/* Acceptance signature block (quotes) */}
            {signatureBlock && (
              <div style={{ marginBottom: 28 }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#222',
                    marginBottom: 12,
                  }}
                >
                  {signatureBlock.title}
                </div>
                <div style={{ display: 'flex', gap: 40 }}>
                  <div style={{ fontSize: 9, color: '#444' }}>
                    {signatureBlock.dateLabel}:{' '}
                    <span
                      style={{
                        display: 'inline-block',
                        width: 110,
                        borderBottom: '1px solid #999',
                      }}
                    >
                      &nbsp;
                    </span>
                  </div>
                  <div style={{ fontSize: 9, color: '#444' }}>
                    {signatureBlock.signatureLabel}:{' '}
                    <span
                      style={{
                        display: 'inline-block',
                        width: 150,
                        borderBottom: '1px solid #999',
                      }}
                    >
                      &nbsp;
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div style={{ marginTop: 'auto' }}>
              {thankYouNote && (
                <div
                  style={{
                    fontSize: 9,
                    color: '#888',
                    fontStyle: 'italic',
                    marginBottom: footerNote ? 6 : 14,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {thankYouNote}
                </div>
              )}
              {footerNote && (
                <div
                  style={{
                    fontSize: 8,
                    color: '#999',
                    marginBottom: 14,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {footerNote}
                </div>
              )}
              {!showQrBillZone && (
                <div
                  style={{
                    borderTop: '1px solid #e0e0e0',
                    paddingTop: 10,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    fontSize: 8,
                    color: '#999',
                  }}
                >
                  <div style={{ textAlign: 'right' }}>Page 1</div>
                </div>
              )}
              {showQrBillZone && (
                <div
                  style={{
                    height: 105 * (595 / 210), // 105mm scaled to A4 preview width
                    border: '1.5px dashed #ccc',
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#bbb',
                    fontSize: 11,
                    letterSpacing: 0.5,
                    marginTop: 8,
                  }}
                >
                  {l.qrBill}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
